'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface HorizontalCarouselProps {
  children: React.ReactNode;
  showProgress?: boolean;
  accentColor?: string;
  /** Background color for fade edges — must match parent */
  fadeBg?: string;
  className?: string;
}

export function HorizontalCarousel({
  children,
  showProgress,
  accentColor = '#3d8b7a',
  fadeBg = '#ffffff',
  className = '',
}: HorizontalCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [viewRatio, setViewRatio] = useState(1);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll > 0) {
      setScrollProgress(el.scrollLeft / maxScroll);
      setViewRatio(el.clientWidth / el.scrollWidth);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir === 'left' ? -el.clientWidth * 0.75 : el.clientWidth * 0.75,
      behavior: 'smooth',
    });
  };

  return (
    <div className={`group relative ${className}`}>
      {/* Fade edges */}
      {canScrollLeft && (
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16"
          style={{ background: `linear-gradient(to right, ${fadeBg}, transparent)` }}
        />
      )}
      {canScrollRight && (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16"
          style={{ background: `linear-gradient(to left, ${fadeBg}, transparent)` }}
        />
      )}

      {/* Nav arrows — visible on hover */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll('left')}
          className="absolute top-[38%] left-2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 opacity-0 shadow-lg transition-all group-hover:opacity-100 hover:scale-110 hover:bg-white"
        >
          <ChevronLeft className="h-5 w-5 text-gray-700" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll('right')}
          className="absolute top-[38%] right-2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 opacity-0 shadow-lg transition-all group-hover:opacity-100 hover:scale-110 hover:bg-white"
        >
          <ChevronRight className="h-5 w-5 text-gray-700" />
        </button>
      )}

      {/* Scroll container */}
      <div ref={scrollRef} className="scrollbar-hide flex gap-6 overflow-x-auto scroll-smooth">
        {children}
      </div>

      {/* Progress indicator */}
      {showProgress && viewRatio < 1 && (
        <div className="mx-auto mt-8 h-[3px] w-full max-w-2xl overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{
              width: `${Math.max(viewRatio * 100, 20)}%`,
              marginLeft: `${scrollProgress * (100 - Math.max(viewRatio * 100, 20))}%`,
              backgroundColor: accentColor,
            }}
          />
        </div>
      )}
    </div>
  );
}
